import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'

type MediaDevicesContextType = {
  hasPermission: boolean
  hasVideoPermission: boolean
  isCheckingPermission: boolean
  requestMicrophonePermission: () => Promise<void>
  requestVideoPermission: () => Promise<void>
}

const MediaDevicesContext = createContext<MediaDevicesContextType | null>(null)

export function MediaDevicesProvider({ children }: { children: ReactNode }) {
  const [hasPermission, setHasPermission] = useState(false)
  const [hasVideoPermission, setHasVideoPermission] = useState(false)
  const [isCheckingPermission, setIsCheckingPermission] = useState(false)

  const requestVideoPermission = async () => {
    setIsCheckingPermission(true)
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({
        audio: true
      })
      setHasVideoPermission(true)

      stream.getTracks().forEach((track) => track.stop())
    } catch (error) {
      setHasVideoPermission(false)
      console.error('Camera permission denied:', error)
    } finally {
      setIsCheckingPermission(false)
    }
  }

  const requestMicrophonePermission = async (): Promise<void> => {
    setIsCheckingPermission(true)
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({
        audio: true
      })
      setHasPermission(true)

      stream.getTracks().forEach((track) => track.stop())
    } catch (error) {
      setHasPermission(false)
      console.error('Microphone permission denied:', error)
    } finally {
      setIsCheckingPermission(false)
    }
  }

  useEffect(() => {
    navigator.permissions
      ?.query({
        name: 'microphone' as PermissionName
      })
      .then((permissionStatus) => {
        setHasPermission(permissionStatus.state === 'granted')
      })

    navigator.permissions
      ?.query({
        name: 'camera' as PermissionName
      })
      .then((permissionStatus) => {
        setHasVideoPermission(permissionStatus.state === 'granted')
      })
  }, [])

  return (
    <MediaDevicesContext.Provider
      value={{
        hasPermission,
        hasVideoPermission,
        isCheckingPermission,
        requestMicrophonePermission,
        requestVideoPermission
      }}
    >
      {children}
    </MediaDevicesContext.Provider>
  )
}

export function useMediaDevices() {
  const context = useContext(MediaDevicesContext)
  if (!context) {
    throw new Error('useMediaDevices must be used within MediaDevicesProvider')
  }
  return context
}
